import React, {
  MutableRefObject,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { Tour, TourProps } from 'antd';
import {
  selectCurrentUserId,
  selectSessionSelector,
} from '@mm/state/selectors/userSelector';
import { useSelector } from 'react-redux';
import { useUsersCRUDActions } from '@mm/state/actions/UsersCRUDActions';
import { selectActiveCompanyId } from '@mm/state/selectors/viewSelectors';
import { useResolutionInfo } from '@mm/hooks';
import { MOBILE_THRESHOLD } from '../../../../../../constants/screenSize';
import styles from './AutomationSettingsTour.module.css';
import {
  MailOutlined,
  MinusCircleOutlined,
  UserDeleteOutlined,
} from '@ant-design/icons';

export type AutomationSettingsTourRefs = {
  settingsButtonRef: MutableRefObject<HTMLButtonElement | null>;
};

type Props = { open?: boolean };

export const AutomationSettingsTour = React.forwardRef<
  AutomationSettingsTourRefs,
  Props
>((props, ref) => {
  const companyId = useSelector(selectActiveCompanyId);
  const currentUserId = useSelector(selectCurrentUserId);
  const session = useSelector(selectSessionSelector);
  const userAttributes = session?.userAttributes;
  const settingsButtonRef = useRef<HTMLButtonElement | null>(null);

  const { updateUserAttributes } = useUsersCRUDActions();

  const { screenWidth } = useResolutionInfo();
  const isMobile = screenWidth <= MOBILE_THRESHOLD;

  useImperativeHandle(ref, () => ({
    settingsButtonRef,
  }));

  const [open, setOpen] = useState<boolean>(false);
  const showTour = !userAttributes?.seenTour?.automationSettings && !isMobile;

  const steps: TourProps['steps'] = [
    {
      title: <div className={styles.stepTitle}>Automation Settings</div>,
      description: (
        <div>
          <div className={styles.stepDescription}>
            Specify sender emails, suppression and exclusion rules.
          </div>
          <div className={styles.buttons}>
            <div className={styles.button}>
              <MailOutlined className={styles.btnIcon} />
              Email Senders
            </div>
            <div className={styles.button}>
              <UserDeleteOutlined className={styles.btnIcon} />
              Suppression
            </div>
            <div className={styles.button}>
              <MinusCircleOutlined className={styles.btnIcon} />
              Exclusion
            </div>
          </div>
        </div>
      ),
      target: settingsButtonRef.current,
      placement: 'leftBottom',
      nextButtonProps: {
        children: <div>Got It</div>,
        className: styles.finishButton,
      },
    },
  ];

  useEffect(() => {
    if (showTour) {
      setOpen(true);
      updateUserAttributes(companyId, currentUserId, {
        seenTour: { ...userAttributes.seenTour, automationSettings: true },
      });
    }
  }, []);

  if (!open) {
    return <></>;
  }

  return (
    <Tour
      rootClassName={styles.tour}
      open={open}
      onClose={() => setOpen(false)}
      steps={steps}
      mask={false}
    />
  );
});
